import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { isTrustedPublisher, hasCreatedProfile } from '../services/galleryService';
import { getPublicProfile } from '../services/profileService';
import { getDatabase, ref, get } from 'firebase/database';
import { Heart, Instagram, ArrowLeft, UserX, Settings, Palette, Search, Tag, X } from 'lucide-react';
import { MOODS, parseMoods } from '../utils/moods';
import { MoodChips, MOOD_COLORS, SELECTED_COLORS } from '../components/MoodPicker';
import AvatarDisplay from '../components/AvatarDisplay';
import ProfileOnboardingModal from '../components/ProfileOnboardingModal';
import ProfileEditModal from '../components/ProfileEditModal';
import ProfilePaletteModal from '../components/ProfilePaletteModal';

// ─── TikTok icon ──────────────────────────────────────────────────────────────

function TikTokIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z" />
    </svg>
  );
}

function displayHeartCount(count) {
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return count;
}

// ─── Palette card ─────────────────────────────────────────────────────────────

function ProfilePaletteCard({ palette }) {
  const colors = palette.previewColors || [];
  const moodNames = parseMoods(palette.moods);
  return (
    <div className="group w-full bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 transition-all duration-200 overflow-hidden">
      <div className="flex h-20 w-full overflow-hidden">
        {colors.map((hex, i) => (
          <div
            key={i}
            className="flex-1 transition-transform duration-200 group-hover:scale-y-105"
            style={{ backgroundColor: hex }}
          />
        ))}
        {colors.length === 0 && <div className="flex-1 bg-slate-100" />}
      </div>
      <div className="px-3 py-2">
        <p className="text-sm font-semibold text-slate-800 truncate leading-tight">{palette.name}</p>
        {moodNames.length > 0 && (
          <div className="mt-1">
            <MoodChips moodNames={moodNames} />
          </div>
        )}
        <div className="flex items-center justify-between gap-2 mt-1">
          <p className="text-[11px] text-slate-400 truncate min-w-0">{palette.colorCount} colors</p>
          {!palette.hideLikes && (
            <div className="flex items-center gap-1 text-xs text-slate-400 flex-shrink-0">
              <Heart size={11} className="fill-red-400 text-red-400" />
              <span>{displayHeartCount(palette.heartCount || 0)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { setProfileCreated, setProfileAvatar } = useProfile();

  const [profile, setProfile] = useState(null);
  const [palettes, setPalettes] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [ownerChecked, setOwnerChecked] = useState(false);
  const [selectedPalette, setSelectedPalette] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMoodFilters, setActiveMoodFilters] = useState([]);
  const [moodFilterOpen, setMoodFilterOpen] = useState(false);

  const filteredPalettes = useMemo(() => {
    let result = palettes;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q));
    }
    if (activeMoodFilters.length > 0) {
      result = result.filter(p => {
        const paletteMoods = parseMoods(p.moods);
        return activeMoodFilters.some(m => paletteMoods.includes(m));
      });
    }
    return result;
  }, [palettes, searchQuery, activeMoodFilters]);

  // Load profile data
  useEffect(() => {
    if (!username) return;
    setLoadingProfile(true);
    getPublicProfile(username).then(result => {
      if (!result.success) {
        setNotFound(true);
      } else {
        setProfile(result.profile);
      }
      setLoadingProfile(false);
    });
  }, [username]);

  // Load palettes from publicPalettes, filter by @username
  useEffect(() => {
    if (!username) return;
    const db = getDatabase();
    Promise.all([
      get(ref(db, 'publicPalettes/top')),
      get(ref(db, 'publicPalettes/recent')),
    ]).then(([topSnap, recentSnap]) => {
      const top = topSnap.exists() ? Object.values(topSnap.val()) : [];
      const recent = recentSnap.exists() ? Object.values(recentSnap.val()) : [];
      const seen = new Set();
      const filtered = [...top, ...recent].filter(p => {
        if (p.authorName !== `@${username}`) return false;
        if (seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
      });
      filtered.sort((a, b) => (b.heartCount || 0) - (a.heartCount || 0));
      setPalettes(filtered);
    });
  }, [username]);

  // Determine owner + onboarding
  useEffect(() => {
    if (authLoading || !username) return;
    if (!user?.uid || user.username !== username) {
      setIsOwner(false);
      setOwnerChecked(true);
      return;
    }
    isTrustedPublisher(user.uid).then(trusted => {
      setIsOwner(trusted);
      if (trusted) {
        hasCreatedProfile(user.uid).then(created => {
          if (!created) setShowOnboarding(true);
          setOwnerChecked(true);
        });
      } else {
        setOwnerChecked(true);
      }
    });
  }, [user, authLoading, username]);

  const handleProfileCreated = (profileData) => {
    setProfile({ username, ...profileData });
    setNotFound(false);
    setShowOnboarding(false);
    setProfileCreated(true);
    setProfileAvatar(profileData.avatar ?? 0);
  };

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (authLoading || loadingProfile || !ownerChecked) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
      </div>
    );
  }

  // ── Not found ────────────────────────────────────────────────────────────────

  if (notFound && !showOnboarding) {
    return (
      <div className="pt-20 flex flex-col items-center gap-4 text-center px-4">
        <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center">
          <UserX size={36} className="text-slate-300" />
        </div>
        <div>
          <p className="text-lg font-semibold text-slate-700">Profile not found</p>
          <p className="text-sm text-slate-400 mt-1">This profile does not exist.</p>
        </div>
        <button
          onClick={() => navigate('/gallery')}
          className="mt-1 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
        >
          <ArrowLeft size={14} /> Back to gallery
        </button>
      </div>
    );
  }

  // ── Profile ──────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-4xl mx-auto animate-fadeOnly">
      <ProfileOnboardingModal
        isOpen={showOnboarding}
        username={username}
        uid={user?.uid}
        onClose={() => { setShowOnboarding(false); navigate('/'); }}
        onProfileCreated={handleProfileCreated}
      />
      <ProfileEditModal
        isOpen={showEdit}
        username={username}
        profile={profile}
        onClose={() => setShowEdit(false)}
        onSaved={(savedData) => {
          setProfile(prev => ({ ...prev, ...savedData }));
          if (savedData.avatar !== undefined) setProfileAvatar(savedData.avatar ?? 0);
        }}
        onDeleted={() => {
          setProfileCreated(false);
          setProfileAvatar(null);
          navigate('/');
        }}
      />

      {/* ── Profile header ────────────────────────────────────────────────────── */}
      <div className="px-2 pt-6 pb-4">

        {/* Top row: avatar + name/description + edit */}
        <div className="flex items-center gap-4">
          <div className="shrink-0 shadow-sm rounded-full">
            <AvatarDisplay avatar={profile?.avatar} username={username} size="lg" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h1 className="text-xl sm:text-2xl font-display font-bold text-slate-600 animate-pulse-soft leading-tight">
                @{username}
              </h1>
              {isOwner && (
                <button
                  onClick={() => setShowEdit(true)}
                  className="shrink-0 p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <Settings size={14} />
                </button>
              )}
            </div>
            {profile?.description && (
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                {profile.description}
              </p>
            )}
          </div>
        </div>

        {/* Socials row: centered, spaced */}
        {(profile?.tiktok || profile?.instagram) && (
          <div className="flex items-center justify-center gap-6 mt-4">
            {profile.tiktok && (
              <a
                href={`https://tiktok.com/@${profile.tiktok}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-full text-xs font-medium text-slate-600 hover:text-slate-900 hover:border-slate-300 hover:bg-slate-50 transition-all"
              >
                <TikTokIcon size={12} />
                @{profile.tiktok}
              </a>
            )}
            {profile.instagram && (
              <a
                href={`https://instagram.com/${profile.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-full text-xs font-medium text-slate-600 hover:text-slate-900 hover:border-slate-300 hover:bg-slate-50 transition-all"
              >
                <Instagram size={12} />
                @{profile.instagram}
              </a>
            )}
          </div>
        )}
      </div>

      {/* ── Divider ───────────────────────────────────────────────────────────── */}
      <div className="border-t border-slate-100 mx-2 mb-4" />

      {/* ── Palette grid ──────────────────────────────────────────────────────── */}
      <div className="px-2 pb-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
            Palettes
          </h2>
          {/* {palettes.length > 0 && (
            <span className="text-xs text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full font-medium">
              {palettes.length}
            </span>
          )} */}
        </div>

        {palettes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-16 h-16 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center">
              <Palette size={28} className="text-slate-300" />
            </div>
            <p className="text-sm text-slate-400">No public palettes yet.</p>
            {isOwner && (
              <button
                onClick={() => navigate('/palettes')}
                className="text-xs text-purple-500 hover:text-purple-700 underline underline-offset-2 transition-colors"
              >
                Head to My Palettes to share your work
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Search + mood filter */}
            <div className="space-y-2 mb-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search palettes…"
                    className="w-full pl-9 pr-9 py-2 text-xs border border-slate-200 rounded-xl bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 focus:border-transparent transition-all"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      <X size={14} />
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setMoodFilterOpen(v => !v)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 ${
                    moodFilterOpen || activeMoodFilters.length > 0
                      ? 'bg-purple-50 border-purple-300 text-purple-700'
                      : 'bg-white/80 backdrop-blur-sm border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                  title="Filter by mood"
                >
                  <Tag size={14} />
                  {activeMoodFilters.length > 0 && <span className="text-xs font-semibold">{activeMoodFilters.length}</span>}
                </button>
              </div>

              {moodFilterOpen && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {MOODS.map(mood => {
                      const active = activeMoodFilters.includes(mood);
                      return (
                        <button
                          key={mood}
                          onClick={() => setActiveMoodFilters(prev =>
                            active ? prev.filter(m => m !== mood) : [...prev, mood]
                          )}
                          className={`text-xs font-medium px-2 py-0.5 rounded-full border transition-all ${
                            active
                              ? (SELECTED_COLORS[mood] || 'bg-slate-700 text-white border-slate-700')
                              : (MOOD_COLORS[mood] || 'bg-slate-100 text-slate-500 border-slate-200') + ' opacity-50 hover:opacity-80'
                          }`}
                        >
                          {mood}
                        </button>
                      );
                    })}
                  </div>
                  {activeMoodFilters.length > 0 && (
                    <button onClick={() => setActiveMoodFilters([])} className="text-xs text-slate-400 hover:text-slate-600 underline">
                      Clear all
                    </button>
                  )}
                </div>
              )}
            </div>

            {filteredPalettes.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-10">No palettes match your search.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {filteredPalettes.map(p => (
                  <button key={p.id} onClick={() => setSelectedPalette(p)} className="text-left w-full">
                    <ProfilePaletteCard palette={p} />
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {selectedPalette && (
        <ProfilePaletteModal
          palette={selectedPalette}
          isOwner={isOwner}
          onClose={() => setSelectedPalette(null)}
        />
      )}
    </div>
  );
}
